import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Bell, Clock } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b7533cee-6cc1-4a41-9e88-4b20184fec89/artifacts/eixt3fs2_STM%20mobile%20logo.png';

const Blog = () => {
  // SEO structured data for blog page
  const blogStructuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "ScopeTheMarket Blog",
    "description": "Stock market insights, trading tips, and anomaly detection guides from ScopeTheMarket",
    "url": "https://scopethemarket.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "ScopeTheMarket",
      "url": "https://scopethemarket.com"
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F1A' }}>
      <SEOHead
        title="Stock Market Blog | Trading Insights & Guides | ScopeTheMarket"
        description="Read the latest stock market insights, trading guides, and anomaly detection tips from ScopeTheMarket. Stay informed about S&P 500 market trends."
        canonical="https://scopethemarket.com/blog"
        ogTitle="ScopeTheMarket Blog – Stock Market Insights"
        ogDescription="Trading guides, market analysis, and anomaly detection tips for smarter stock research."
        structuredData={blogStructuredData}
      />
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-10"
          style={{
            background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)',
            top: '-200px',
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        />
      </div>

      {/* Navigation */}
      <nav className="relative z-50 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="ScopeTheMarket" 
                className="h-9 w-auto"
                style={{ filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))' }}
              />
              <span className="text-xl font-semibold tracking-tight" style={{ color: '#F8FAFC' }}>
                ScopeTheMarket
              </span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/pricing" className="text-sm font-medium transition-colors hover:text-white" style={{ color: '#94A3B8' }}>
                Pricing
              </Link>
              <Link to="/about" className="text-sm font-medium transition-colors hover:text-white" style={{ color: '#94A3B8' }}>
                About
              </Link>
              <Link to="/blog" className="text-sm font-medium" style={{ color: '#F8FAFC' }}>
                Blog
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium transition-colors hover:text-white"
                style={{ color: '#94A3B8' }}
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="px-5 py-2.5 text-sm font-medium rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  color: 'white'
                }}
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 py-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back link */}
          <Link 
            to="/"
            className="inline-flex items-center gap-2 mb-8 text-sm transition-colors hover:text-white"
            style={{ color: '#64748B' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="text-center mb-16">
            <h1 
              className="text-4xl sm:text-5xl font-bold mb-4"
              style={{ color: '#F8FAFC' }}
            >
              Blog
            </h1>
            <p className="text-base md:text-lg" style={{ color: '#94A3B8' }}>
              Market insights and anomaly breakdowns
            </p>
          </div>

          {/* Coming Soon Card */}
          <div 
            className="p-12 md:p-16 rounded-2xl text-center"
            style={{
              background: 'rgba(15, 23, 42, 0.6)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              backdropFilter: 'blur(12px)'
            }}
            data-testid="blog-coming-soon"
          >
            <div 
              className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-8"
              style={{ 
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}
            >
              <FileText className="w-10 h-10" style={{ color: '#3B82F6' }} />
            </div>
            
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#F8FAFC' }}>
              Coming Soon
            </h2>
            <p className="text-lg mb-8 max-w-md mx-auto leading-relaxed" style={{ color: '#94A3B8' }}>
              We're working on market insights and anomaly breakdowns to help you understand market movements better.
            </p>

            {/* Features Preview */}
            <div className="grid md:grid-cols-2 gap-4 max-w-lg mx-auto mb-10">
              <div 
                className="p-4 rounded-xl flex items-center gap-3"
                style={{
                  background: 'rgba(59, 130, 246, 0.05)',
                  border: '1px solid rgba(59, 130, 246, 0.1)'
                }}
              >
                <Bell className="w-5 h-5" style={{ color: '#3B82F6' }} />
                <span className="text-sm" style={{ color: '#CBD5E1' }}>Weekly Market Recaps</span>
              </div>
              <div 
                className="p-4 rounded-xl flex items-center gap-3"
                style={{
                  background: 'rgba(59, 130, 246, 0.05)',
                  border: '1px solid rgba(59, 130, 246, 0.1)'
                }}
              >
                <Clock className="w-5 h-5" style={{ color: '#3B82F6' }} />
                <span className="text-sm" style={{ color: '#CBD5E1' }}>Anomaly Deep Dives</span>
              </div>
            </div>

            {/* CTA */}
            <Link
              to="/app"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-xl transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.25)'
              }}
            >
              View Live Anomalies Instead
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-6" style={{ borderTop: '1px solid rgba(148, 163, 184, 0.1)' }}>
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-center" style={{ color: '#475569' }}>
            © {new Date().getFullYear()} ScopeTheMarket. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
